import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { fmtDate } from '@/lib/format';
import { Users, Building2, FileSignature, Receipt, Shield, KeyRound, ScrollText, Briefcase, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

const ICONS = {
  users: Users, owners: KeyRound, consultants: Shield, agents: Briefcase, tenants: Users,
  properties: Building2, units: Building2, leases: FileSignature,
  rent_demands: Receipt, payments: Receipt,
  pending_kyc_total: ShieldCheck,
};

const LABELS = {
  users: 'Users',
  owners: 'Owners',
  consultants: 'Consultants',
  agents: 'Agents',
  tenants: 'Tenants',
  properties: 'Properties',
  units: 'Units',
  leases: 'Active Leases',
  rent_demands: 'Open Rent Demands',
  payments: 'Open Payments',
  pending_kyc_total: 'Pending KYC',
};

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/dashboard/admin').then(r => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="text-sm text-muted-foreground py-12 text-center">Loading…</div>;
  const counts = data.counts || {};
  const pendingKyc = data.pending_kyc || {};

  return (
    <div className="space-y-5">
      <PageHeader title="Super Admin Dashboard" subtitle="System-wide overview, KYC queue, and audit trail" />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {Object.entries(counts).map(([k, v]) => {
          const I = ICONS[k] || Users;
          const isAlert = k === 'pending_kyc_total' && v > 0;
          return (
            <Card key={k} className={`p-4 ${isAlert ? 'border-amber-300 bg-amber-50/50' : ''}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-muted-foreground">{LABELS[k] || k.replace(/_/g, ' ')}</div>
                <I className={`h-4 w-4 ${isAlert ? 'text-amber-600' : 'text-muted-foreground'}`} />
              </div>
              <div className={`text-2xl font-display font-semibold mt-1 ${isAlert ? 'text-amber-700' : ''}`}>{v}</div>
            </Card>
          );
        })}
      </div>

      {counts.pending_kyc_total > 0 && (
        <Card className="p-4 border-amber-300 bg-amber-50/40">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <div className="text-sm font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-amber-700" /> KYC Approvals pending</div>
              <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-2">
                {Object.entries(pendingKyc).map(([k, v]) => (
                  <Badge key={k} variant="outline" className="bg-white capitalize">{k}: {v}</Badge>
                ))}
              </div>
            </div>
            <Link to="/admin/kyc" className="inline-flex items-center justify-center h-9 px-3 rounded-md bg-amber-600 text-white text-sm font-medium hover:bg-amber-700">
              Review KYC queue →
            </Link>
          </div>
        </Card>
      )}

      <Card className="p-4">
        <div className="text-sm font-semibold flex items-center gap-2"><ScrollText className="h-4 w-4" /> Recent activity</div>
        <div className="divide-y mt-2 max-h-[420px] overflow-auto">
          {(!data.recent_activity || data.recent_activity.length === 0) && (
            <div className="py-6 text-center text-xs text-muted-foreground">No activity yet.</div>
          )}
          {data.recent_activity?.map((a) => (
            <div key={a.id} className="py-2 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{a.user_name}</span>
                <span className="text-xs text-muted-foreground capitalize">({a.role})</span>
                <span className="text-muted-foreground">{a.action}</span>
                <span className="font-mono text-xs">{a.entity}</span>
              </div>
              <div className="text-xs text-muted-foreground">{a.detail || ''} · {fmtDate(a.created_at)}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
